import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { ContentLoader } from '@frontend/design-system';

// pdfjs-dist references DOMMatrix at module load, which breaks jsdom/happy-dom tests.
// Lazy-load it so importing this file is test-safe.
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      return mod;
    });
  }
  return pdfjsPromise;
};

export type PdfPageThumbnailProps = {
  src: string;
  pageNumber?: number;
  className?: string;
};

export const PdfPageThumbnail = ({ src, pageNumber = 1, className }: PdfPageThumbnailProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const node = wrapperRef.current;
    const update = () => setWidth(node.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!src || width === 0 || !canvasRef.current) return;
    const canvas = canvasRef.current;
    let cancelled = false;
    let renderTask: { cancel: () => void } | null = null;
    let loadedDoc: PDFDocumentProxy | null = null;
    setIsLoading(true);
    (async () => {
      const { getDocument } = await loadPdfjs();
      if (cancelled) return;
      try {
        loadedDoc = await getDocument({ url: src }).promise;
      } catch {
        if (!cancelled) setIsLoading(false);
        return;
      }
      if (cancelled) {
        loadedDoc?.destroy();
        return;
      }
      const page = await loadedDoc.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const dpr = window.devicePixelRatio || 1;
      const scale = (width / base.width) * dpr;
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const task = page.render({ canvasContext: ctx, viewport, canvas });
      renderTask = task;
      try {
        await task.promise;
        if (!cancelled) setIsLoading(false);
      } catch (err) {
        if (!(err instanceof Error) || err.name !== 'RenderingCancelledException') throw err;
      }
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
      loadedDoc?.destroy();
    };
  }, [src, pageNumber, width]);

  return (
    <div ref={wrapperRef} className={className}>
      <canvas ref={canvasRef} css={{ display: 'block' }} />
      <ContentLoader size='small' show={isLoading} />
    </div>
  );
};
